import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type { TopologyLayout } from '../../types'

interface TopologyLayoutModalProps {
  isOpen: boolean
  layout?: TopologyLayout | null
  onClose: () => void
  onSave: (data: { name: string; description?: string; isShared: boolean; isDefault: boolean }) => void
  isAdmin: boolean
}

export function TopologyLayoutModal({ isOpen, layout, onClose, onSave, isAdmin }: TopologyLayoutModalProps) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isShared, setIsShared] = useState(false)
  const [isDefault, setIsDefault] = useState(false)

  useEffect(() => {
    if (layout) {
      setName(layout.name)
      setDescription(layout.description || '')
      setIsShared(layout.isShared)
      setIsDefault(layout.isDefault)
    } else {
      setName('')
      setDescription('')
      setIsShared(false)
      setIsDefault(false)
    }
  }, [layout, isOpen])

  if (!isOpen) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    onSave({
      name: name.trim(),
      description: description.trim() || undefined,
      isShared,
      isDefault,
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {layout ? t('topologyLayouts.edit') : t('topologyLayouts.create')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
            aria-label={t('common.cancel')}
          >
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="tl-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('topologyLayouts.name')}
            </label>
            <input
              id="tl-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full px-3 py-2 border dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm"
              placeholder={t('topologyLayouts.namePlaceholder')}
              required
              autoFocus
              maxLength={200}
            />
          </div>
          <div>
            <label htmlFor="tl-desc" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('topologyLayouts.description')}
            </label>
            <textarea
              id="tl-desc"
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full px-3 py-2 border dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg text-sm"
              placeholder={t('topologyLayouts.descriptionPlaceholder')}
              rows={2}
              maxLength={500}
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={e => setIsDefault(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              {t('topologyLayouts.setDefault')}
            </label>
            {isAdmin && (
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={isShared}
                  onChange={e => setIsShared(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                {t('topologyLayouts.shared')}
              </label>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!name.trim()}
              className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
            >
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
